import { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import GenericPopup from '../../../components/GenericPopup';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/ClerkAuthContext';
import { useClerk } from '@clerk/clerk-react';
import { promoCodeService } from '../../api/services';

const PopupTitle = styled.h3`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  text-align: center;
`;

const CartItems = styled.div`
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: ${props => props.theme.spacing.lg};
  
  /* Ensure scrolling works on all devices */
  -webkit-overflow-scrolling: touch;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.neutral.light};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.accent.main};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.accent.deep};
  }
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.neutral.light};
  opacity: ${props => props.$quantity === 0 ? 0.5 : 1};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  margin-right: ${props => props.theme.spacing.md};
`;

const ItemName = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ItemDetails = styled.div`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.7;
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ItemPrice = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent.main};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const QuantityButton = styled.button`
  background: ${props => props.theme.colors.primary.background};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.primary.buttons};
  font-size: 1rem;
  font-weight: 600;
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: ${props => props.theme.colors.primary.background};
      color: ${props => props.theme.colors.primary.buttons};
    }
  }
`;

const QuantityDisplay = styled.div`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  min-width: 30px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.5;
  cursor: pointer;
  font-size: 1.2rem;
  padding: ${props => props.theme.spacing.xs};
  margin-left: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    opacity: 1;
    color: ${props => props.theme.colors.accent.main};
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.primary.text};
  opacity: 0.7;
  font-family: ${props => props.theme.typography.secondary.fontFamily};
`;

const CartSummary = styled.div`
  background: ${props => props.theme.colors.neutral.light};
  border-radius: 8px;
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xs};
  
  &:last-child {
    margin-bottom: 0;
    padding-top: ${props => props.theme.spacing.xs};
    border-top: 1px solid ${props => props.theme.colors.neutral.medium};
    font-weight: 600;
    font-size: 1.1rem;
  }
`;

const SummaryLabel = styled.span`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  color: ${props => props.theme.colors.primary.text};
`;

const SummaryValue = styled.span`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing.md};
`;

const ActionButton = styled.button`
  font-family: ${props => props.theme.typography.button.fontFamily};
  font-size: ${props => props.theme.typography.button.fontSize};
  font-weight: ${props => props.theme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme.typography.button.letterSpacing};
  text-transform: ${props => props.theme.typography.button.textTransform};
  
  background: ${props => props.$primary ? props.theme.colors.primary.buttons : 'transparent'};
  color: ${props => props.$primary ? props.theme.colors.primary.background : props.theme.colors.primary.buttons};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  text-decoration: none;
  
  &:hover {
    background: ${props => props.theme.colors.primary.buttons};
    color: ${props => props.theme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
    }
  }
`;

const LoginMessage = styled.div`
  background: ${props => props.theme.colors.accent.light};
  border: 1px solid ${props => props.theme.colors.accent.main};
  border-radius: 8px;
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  text-align: center;
  
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme.colors.accent.main};
  font-weight: 500;
`;

const LoginLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary.buttons};
  text-decoration: underline;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  padding: 0;
  margin: 0;
  
  &:hover {
    color: ${props => props.theme.colors.accent.deep};
    text-decoration: none;
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary.buttons};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const CheckoutSection = styled.div`
  opacity: ${props => props.$disabled ? 0.4 : 1};
  pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
  transition: ${props => props.theme.transitions.default};
`;

const PromoCodeSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.neutral.lightest};
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.neutral.light};
`;

const PromoCodeTitle = styled.h4`
  font-family: ${props => props.theme.typography.primary.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary.text};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
`;

const PromoCodeInput = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const PromoCodeField = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.neutral.medium};
  border-radius: 4px;
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary.buttons};
  }
  
  &:disabled {
    background: ${props => props.theme.colors.neutral.light};
    opacity: 0.6;
  }
`;

const PromoCodeButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.primary.buttons};
  color: ${props => props.theme.colors.primary.background};
  border: 1px solid ${props => props.theme.colors.primary.buttons};
  border-radius: 4px;
  font-family: ${props => props.theme.typography.button.fontFamily};
  font-size: 0.85rem;
  font-weight: ${props => props.theme.typography.button.fontWeight};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  
  &:hover {
    background: ${props => props.theme.colors.primary.background};
    color: ${props => props.theme.colors.primary.buttons};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      background: ${props => props.theme.colors.primary.buttons};
      color: ${props => props.theme.colors.primary.background};
    }
  }
`;

const PromoCodeMessage = styled.div`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${props => props.$error ? props.theme.colors.accent.main : props.theme.colors.primary.text};
  margin-top: ${props => props.theme.spacing.xs};
  opacity: ${props => props.$error ? 1 : 0.7};
`;

const AppliedPromoCode = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.accent.bright};
  border-radius: 4px;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const PromoCodeInfo = styled.div`
  font-family: ${props => props.theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme.colors.accent.main};
  font-weight: 600;
`;

const RemovePromoButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.accent.main};
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: underline;
  
  &:hover {
    text-decoration: none;
  }
`;

const ItemDiscount = styled.span`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.accent.main};
  margin-left: ${props => props.theme.spacing.xs};
  font-weight: 600;
`;

const CartPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { 
    cartItems, 
    updateCartItem, 
    removeFromCart, 
    clearCart, 
    getCartTotal, 
    getCartItemCount,
    promoCode,
    promoDiscount,
    applyPromoCode,
    removePromoCode,
    getCartTotalWithPromo,
    getItemDiscount
  } = useCart();
  const { user } = useAuth();
  const clerk = useClerk();
  
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoCodeMessage, setPromoCodeMessage] = useState('');
  const [promoCodeError, setPromoCodeError] = useState(false);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  const handleQuantityChange = (cartId, newQuantity) => {
    updateCartItem(cartId, newQuantity);
  };

  const handleRemoveItem = (cartId) => {
    removeFromCart(cartId);
  };

  const handleLoginClick = () => {
    onClose(); // Close the cart popup
    // Use Clerk's hosted sign-in page (redirect method is more reliable)
    if (clerk && clerk.redirectToSignIn) {
      try {
        clerk.redirectToSignIn({
          redirectUrl: window.location.href
        });
      } catch (error) {
        console.error('Failed to redirect to sign-in:', error);
        // Try modal as fallback
        if (clerk.openSignIn) {
          try {
            clerk.openSignIn();
          } catch (modalError) {
            console.error('Failed to open sign-in modal:', modalError);
          }
        }
      }
    } else if (clerk && clerk.openSignIn) {
      // Fallback to modal method
      try {
        clerk.openSignIn();
      } catch (error) {
        console.error('Failed to open sign-in modal:', error);
      }
    }
  };

  const handleApplyPromoCode = async () => {
    if (!promoCodeInput.trim()) {
      setPromoCodeError(true);
      setPromoCodeMessage('Please enter a promo code');
      return;
    }

    setIsApplyingPromo(true);
    setPromoCodeError(false);
    setPromoCodeMessage('');

    try {
      const result = await promoCodeService.applyPromoCode(promoCodeInput.trim(), cartItems);
      
      if (result.success) {
        applyPromoCode(promoCodeInput.trim(), result);
        setPromoCodeMessage(result.message || 'Promo code applied successfully!');
        setPromoCodeInput('');
        setPromoCodeError(false);
      } else {
        setPromoCodeError(true);
        setPromoCodeMessage(result.error || 'Invalid promo code');
      }
    } catch (error) {
      setPromoCodeError(true);
      setPromoCodeMessage('Failed to apply promo code. Please try again.');
    } finally {
      setIsApplyingPromo(false);
    }
  };

  const handleRemovePromoCode = () => {
    removePromoCode();
    setPromoCodeMessage('');
    setPromoCodeError(false);
  };

  const handlePromoCodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleApplyPromoCode();
    }
  };

  const totalItems = getCartItemCount();
  const totalPrice = getCartTotal();
  const totalWithPromo = getCartTotalWithPromo();
  const activeItems = cartItems.filter(item => item.quantity > 0);
  const isLoggedIn = !!user;

  return (
    <GenericPopup isOpen={isOpen} onClose={onClose} maxWidth="500px" fixedHeight={false}>
      <PopupTitle >Shopping Cart ({totalItems} items)</PopupTitle>
      
      {cartItems.length === 0 ? (
        <EmptyCart >
          Your cart is empty
        </EmptyCart>
      ) : (
        <>
          <CartItems >
            {/* Show all items in the order they were added */}
            {cartItems.map((item) => (
              <CartItem key={item.cartId} $quantity={item.quantity} >
                <ItemInfo >
                  <ItemName >{item.name}</ItemName>
                  <ItemDetails >
                    {item.description}
                    {item.isRecurring && <span> • Monthly recurring</span>}
                  </ItemDetails>
                  <ItemPrice >
                    ${(item.isRecurring && item.itemType !== 'membership' ? item.price * 0.95 : item.price).toFixed(2)}
                    {item.quantity > 1 && ` × ${item.quantity}`}
                    {item.isRecurring && '/month'}
                    {(() => {
                      const discount = getItemDiscount(item);
                      if (discount) {
                        return (
                          <ItemDiscount>
                            ({discount.percentage}% off)
                          </ItemDiscount>
                        );
                      }
                      return null;
                    })()}
                  </ItemPrice>
                </ItemInfo>
                
                <QuantityControls >
                  <QuantityButton 
                                       onClick={() => handleQuantityChange(item.cartId, item.quantity - 1)}
                    disabled={item.quantity <= 0}
                  >
                    −
                  </QuantityButton>
                  <QuantityDisplay >{item.quantity}</QuantityDisplay>
                  <QuantityButton  onClick={() => handleQuantityChange(item.cartId, item.quantity + 1)}>
                    +
                  </QuantityButton>
                  <RemoveButton  onClick={() => handleRemoveItem(item.cartId)}>
                    ×
                  </RemoveButton>
                </QuantityControls>
              </CartItem>
            ))}
          </CartItems>

          {activeItems.length > 0 && (
            <>
              {!isLoggedIn && (
                <LoginMessage >
                  Please <LoginLink  onClick={handleLoginClick}>log in</LoginLink> to proceed with checkout
                </LoginMessage>
              )}
              
              <PromoCodeSection>
                <PromoCodeTitle>Promo Code</PromoCodeTitle>
                {promoCode ? (
                  <AppliedPromoCode>
                    <PromoCodeInfo>
                      {promoCode} applied
                    </PromoCodeInfo>
                    <RemovePromoButton onClick={handleRemovePromoCode}>
                      Remove
                    </RemovePromoButton>
                  </AppliedPromoCode>
                ) : (
                  <PromoCodeInput>
                    <PromoCodeField
                      type="text"
                      placeholder="Enter promo code"
                      value={promoCodeInput}
                      onChange={(e) => setPromoCodeInput(e.target.value)}
                      onKeyDown={handlePromoCodeKeyDown}
                      disabled={isApplyingPromo}
                    />
                    <PromoCodeButton
                      onClick={handleApplyPromoCode}
                      disabled={isApplyingPromo || !promoCodeInput.trim()}
                    >
                      {isApplyingPromo ? 'Applying...' : 'Apply'}
                    </PromoCodeButton>
                  </PromoCodeInput>
                )}
                {promoCodeMessage && (
                  <PromoCodeMessage $error={promoCodeError}>
                    {promoCodeMessage}
                  </PromoCodeMessage>
                )}
              </PromoCodeSection>
              
              <CartSummary >
                <SummaryRow >
                  <SummaryLabel >Subtotal</SummaryLabel>
                  <SummaryValue >${totalPrice.toFixed(2)}</SummaryValue>
                </SummaryRow>
                {promoDiscount && promoDiscount.total_discount > 0 && (
                  <SummaryRow >
                    <SummaryLabel >Promo Discount</SummaryLabel>
                    <SummaryValue >-${promoDiscount.total_discount.toFixed(2)}</SummaryValue>
                  </SummaryRow>
                )}
                <SummaryRow >
                  <SummaryLabel >Total</SummaryLabel>
                  <SummaryValue >${totalWithPromo.toFixed(2)}</SummaryValue>
                </SummaryRow>
              </CartSummary>

              <ButtonContainer >
                <ActionButton  onClick={clearCart}>
                  Clear Cart
                </ActionButton>
                <CheckoutSection  $disabled={!isLoggedIn}>
                  <ActionButton  
                    $primary 
                    disabled={!isLoggedIn}
                    onClick={() => {
                      if (isLoggedIn) {
                        onClose();
                        navigate('/checkout');
                      }
                    }}
                  >
                    Checkout
                  </ActionButton>
                </CheckoutSection>
              </ButtonContainer>
            </>
          )}
        </>
      )}
    </GenericPopup>
  );
};

export default CartPopup;